/**
 * Profile Section Component
 * User profile dropdown in header
 */

import React, { useState, useRef } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  ClickAwayListener,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';

import { useAuth } from '../hooks/useAuth';

/**
 * ProfileSection
 * Displays user profile with dropdown menu
 * 
 * @returns {React.ReactNode}
 */
const ProfileSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleProfile = () => {
    navigate('/profile');
    setOpen(false);
  };

  const handleSettings = () => {
    navigate('/settings');
    setOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setOpen(false);
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <ButtonBase
        sx={{ borderRadius: '50px' }}
        ref={anchorRef}
        aria-controls={open ? 'profile-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleToggle}
      >
        <Avatar
          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.username || 'User')}&background=e0f2fe&color=1E88E5&bold=true`}
          sx={{
            ...theme.typography.largeAvatar,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': { opacity: 0.8 }
          }}
          aria-label="User profile"
        />
      </ButtonBase>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-end"
        sx={{
          zIndex: 1300,
        }}
      >
        <Box sx={{
          minWidth: 240,
          overflow: 'visible',
          borderRadius: '12px',
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
          backgroundColor: '#fff',
          border: '1px solid #f1f5f9', // Sharper definition
          position: 'relative',
          mr: 1.5,
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 28,
            width: 12,
            height: 12,
            backgroundColor: '#fff',
            borderTop: '1px solid #f1f5f9',
            borderLeft: '1px solid #f1f5f9',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        }}>
          <ClickAwayListener onClickAway={handleClose}>
            <Box>
              {/* User Info Section */}
              <Box sx={{ pt: 4, pb: 3, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Avatar
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.username || 'User')}&background=f8fafc&color=94a3b8&size=144&bold=true`}
                  sx={{
                    width: 72,
                    height: 72,
                    mb: 2,
                    border: '1px solid #f1f5f9'
                  }}
                />

                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 0.2, letterSpacing: '0.025em' }}>
                  Hi,
                </Typography>

                {/* <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 600, textAlign: 'center', mb: 0.3 }}>
                  {user.name}
                </Typography> */}

                <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 400, mb: 1.5 }}>
                  {user.username}
                </Typography>

                <Box
                  sx={{
                    px: 1.5,
                    py: 0.3,
                    borderRadius: '20px',
                    backgroundColor: 'rgba(33, 150, 243, 0.08)',
                    color: theme.palette.primary.main,
                    
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}
                >
                  {user.role}
                </Box>
              </Box>

              <Divider sx={{ mx: 2, borderColor: '#f1f5f9' }} />

              {/* Logout Section */}
              <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}>
                <ButtonBase
                  onClick={handleLogout}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    width: '100%',
                    justifyContent: 'center',
                    py: 1.4,
                    borderRadius: '8px',
                    color: '#64748b',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: '#f8fafc',
                      color: '#0f172a',
                    },
                  }}
                >
                  <LogoutIcon sx={{  opacity: 0.8 }} />
                  <Typography sx={{ fontWeight: 600,  }}>
                    Logout
                  </Typography>
                </ButtonBase>
              </Box>
            </Box>
          </ClickAwayListener>
        </Box>
      </Popper>
    </>
  );
};

export default ProfileSection;
